'use client';

import { useState, useMemo } from 'react';
import { trips, DifficultyLevel, TripType } from '@/data/mock-data';
import { TripCard } from '@/components/trip-card';
import { CustomTripCTA } from '@/components/CustomTripCTA';
import { Filter } from 'lucide-react';

export default function TripsPage() {
  const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyLevel | 'all'>('all');
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<TripType | 'all'>('all');

  const regions = ['all', ...new Set(trips.map((trip) => trip.region))];

  const filteredTrips = useMemo(() => {
    return trips.filter((trip) => {
      const matchesDifficulty =
        selectedDifficulty === 'all' || trip.difficulty === selectedDifficulty;
      const matchesRegion = selectedRegion === 'all' || trip.region === selectedRegion;
      const matchesType = selectedType === 'all' || trip.tripType === selectedType;
      return matchesDifficulty && matchesRegion && matchesType;
    });
  }, [selectedDifficulty, selectedRegion, selectedType]);

  return (
    <div className="min-h-screen py-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">All Adventures</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Browse our complete collection of guided backpacking trips. Filter by difficulty,
            location, and duration to find your perfect adventure.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-muted-foreground" />
            <span className="font-semibold text-foreground">Filter Trips</span>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Difficulty Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Difficulty</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value as DifficultyLevel | 'all')}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background text-foreground"
              >
                <option value="all">All Levels</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            {/* Region Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Location</label>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background text-foreground"
              >
                {regions.map((region) => (
                  <option key={region} value={region}>
                    {region === 'all' ? 'All Locations' : region}
                  </option>
                ))}
              </select>
            </div>

            {/* Trip Type Filter */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Duration</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as TripType | 'all')}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background text-foreground"
              >
                <option value="all">All Durations</option>
                <option value="day-hike">Day Hikes</option>
                <option value="weekend">Weekend Trips</option>
                <option value="week-long">Week-Long Expeditions</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-muted-foreground">
            Showing {filteredTrips.length} {filteredTrips.length === 1 ? 'trip' : 'trips'}
          </p>
        </div>

        {/* Trip Grid */}
        {filteredTrips.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTrips.map((trip) => (
              <TripCard key={trip.id} trip={trip} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="text-xl text-muted-foreground mb-4">
              No trips match your current filters
            </p>
            <button
              onClick={() => {
                setSelectedDifficulty('all');
                setSelectedRegion('all');
                setSelectedType('all');
              }}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* Custom Trip Planning CTA */}
      <CustomTripCTA />
    </div>
  );
}
